<template>
  <transition name="notes-fade">
    <div
      v-if="visible"
      v-click-outside="closeAndSave"
      class="notes-popover-container"
      :class="['placement-' + notesApi.state.placement]"
      :style="notesStyle"
      @mouseenter="onPopoverEnter"
      @mouseleave="onPopoverLeave"
      @click.stop="switchToEdit"
    >
      <div
        ref="popover"
        class="custom-notes-popover"
        :class="{ 'is-view-mode': !isEditing }"
        :style="customDimensions"
      >
        <div
          ref="editor"
          class="notes-editor"
          :contenteditable="isEditing"
          :placeholder="!localText ? 'Введите текст заметки...' : ''"
          @keydown="handleKeydown"
          @input="onInput"
          @paste="handlePaste"
          @click="handleEditorClick"
        />

        <button
          v-if="isEditing"
          class="delete-btn"
          @click.stop="removeNote"
          title="Удалить заметку"
        >
          <TrashBin />
        </button>

        <span v-if="isEditing" class="char-counter">
          {{ textLength }}/1000
        </span>

        <Resizer
          v-if="isEditing"
          class="notes-resizer-icon"
          @mousedown.prevent="startResize"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import Resizer from "@/assets/icons/resizer.svg";
import TrashBin from "@/assets/icons/trash-bin.svg";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "TreeTableNotesPopover",
  inject: ["notesApi"],
  components: { Resizer, TrashBin },
  data() {
    return {
      localText: "",
      initialText: "",
      debounceTimer: null,
      maxLength: 1000,

      dragWidth: null,
      dragHeight: null,
      isResizing: false,
      startX: 0,
      startY: 0,
      startWidth: 0,
      startHeight: 0,
      startTop: 0,
    };
  },
  computed: {
    ...mapGetters("profile", ["profileId"]),
    ...mapGetters("notes", ["notes", "noteWindowWidth", "noteWindowHeight"]),

    visible() {
      return this.notesApi.state.visible;
    },
    isEditing() {
      return this.notesApi.state.isEditing;
    },
    notesStyle() {
      return this.notesApi.state.style;
    },
    customDimensions() {
      const styles = {};
      const w = this.dragWidth || this.noteWindowWidth;
      const h = this.dragHeight || this.noteWindowHeight;
      if (w) styles.width = `${w}px`;
      if (h) styles.height = `${h}px`;
      return styles;
    },
    row() {
      return this.notesApi.state.row;
    },
    targetProposalNumber() {
      return this.row?.proposalNumber?.proposalNumber || null;
    },
    existingNote() {
      if (!this.targetProposalNumber || !this.notes) return null;
      return this.notes.find(
        (note) => note.proposalNumber === this.targetProposalNumber,
      );
    },
    textLength() {
      return this.localText ? this.localText.length : 0;
    },
  },
  watch: {
    visible(isVisible, wasVisible) {
      if (isVisible) {
        this.initNoteData();
      } else if (wasVisible) {
        this.clearDebounce();
        this.forceSave();
      }
    },
    row(newRow, oldRow) {
      if (this.visible && newRow?.id !== oldRow?.id) {
        this.clearDebounce();
        this.forceSave();
        this.initNoteData();
      }
    },
  },
  methods: {
    ...mapActions("notes", [
      "createProfileNote",
      "updateProfileNote",
      "deleteProfileNote",
      "setNoteWindowSize",
      "updateNoteWindowSize",
    ]),

    initNoteData() {
      this.localText = this.existingNote?.text || "";
      this.initialText = this.localText;

      this.$nextTick(() => {
        this.renderContent();
        if (this.isEditing && this.$refs.editor) {
          this.$refs.editor.focus();
        }
      });
    },

    startResize(e) {
      this.isResizing = true;
      this.startY = e.clientY;
      this.startX = e.clientX;
      this.startWidth = this.$refs.popover.offsetWidth;
      this.startHeight = this.$refs.popover.offsetHeight;
      this.startTop = parseFloat(this.notesStyle.top) || 0;

      document.addEventListener("mousemove", this.onResize);
      document.addEventListener("mouseup", this.stopResize);
    },

    onResize(e) {
      if (!this.isResizing) return;

      const deltaX = e.clientX - this.startX;
      const deltaY = e.clientY - this.startY;

      let newWidth = this.startWidth + deltaX;
      let newHeight;

      if (this.notesApi.state.placement === "top") {
        newHeight = this.startHeight - deltaY;

        if (newWidth < 220) newWidth = 220;
        if (newHeight < 140) newHeight = 140;

        const heightDiff = newHeight - this.startHeight;
        this.notesApi.state.style.top = `${this.startTop - heightDiff}px`;
      } else {
        newHeight = this.startHeight + deltaY;

        if (newWidth < 220) newWidth = 220;
        if (newHeight < 140) newHeight = 140;
      }

      this.dragWidth = newWidth;
      this.dragHeight = newHeight;
    },

    stopResize() {
      this.isResizing = false;
      document.removeEventListener("mousemove", this.onResize);
      document.removeEventListener("mouseup", this.stopResize);

      if (this.dragWidth && this.dragHeight) {
        this.updateNoteWindowSize({
          width: this.dragWidth,
          height: this.dragHeight,
        });
      }

      this.dragWidth = null;
      this.dragHeight = null;
    },

    formatToHtml(text) {
      if (!text) return "";
      const escaped = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

      const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
      return escaped
        .replace(urlRegex, (url) => {
          const href = url.toLowerCase().startsWith("www.")
            ? `http://${url}`
            : url;
          return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="notes-link">${url}</a>`;
        })
        .replace(/\n/g, "<br>");
    },

    renderContent() {
      if (this.$refs.editor) {
        this.$refs.editor.innerHTML = this.formatToHtml(this.localText);
      }
    },

    handleKeydown(e) {
      if (!this.$refs.editor) return;

      const isControlKey =
        e.ctrlKey ||
        e.metaKey ||
        [
          "Backspace",
          "Delete",
          "ArrowLeft",
          "ArrowRight",
          "ArrowUp",
          "ArrowDown",
          "Tab",
          "Home",
          "End",
        ].includes(e.key);

      const selection = window.getSelection();
      const hasSelection = selection && selection.toString().length > 0;

      if (this.textLength >= this.maxLength && !isControlKey && !hasSelection) {
        e.preventDefault();
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        document.execCommand("insertLineBreak");
      }
    },

    getEditorText(element) {
      let text = "";
      const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          text += node.textContent;
        } else if (node.nodeName === "BR") {
          text += "\n";
        } else {
          for (let i = 0; i < node.childNodes.length; i++) {
            walk(node.childNodes[i]);
          }
        }
      };

      for (let i = 0; i < element.childNodes.length; i++) {
        walk(element.childNodes[i]);
      }
      return text;
    },

    onInput() {
      if (!this.$refs.editor) return;

      let caretOffset = this.getCaretOffset(this.$refs.editor);
      let text = this.getEditorText(this.$refs.editor);

      if (text.length > this.maxLength) {
        text = text.slice(0, this.maxLength);
        if (caretOffset > this.maxLength) {
          caretOffset = this.maxLength;
        }
      }

      this.localText = text;
      this.renderContent();
      this.setCaretOffset(this.$refs.editor, caretOffset);

      this.clearDebounce();
      this.debounceTimer = setTimeout(() => {
        this.forceSave();
      }, 500);
    },

    handlePaste(e) {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData(
        "text/plain",
      );
      if (!text) return;

      const selection = window.getSelection();
      const selectedLength = selection ? selection.toString().length : 0;
      const allowedLength = this.maxLength - (this.textLength - selectedLength);

      if (allowedLength <= 0) return;

      const trimmedText = text.slice(0, allowedLength);
      document.execCommand("insertText", false, trimmedText);
    },

    getCaretOffset(element) {
      let caretOffset = 0;
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return 0;

      const range = sel.getRangeAt(0);
      let nodeFound = false;

      const walkNodes = (node) => {
        if (nodeFound) return;

        if (node === range.endContainer) {
          if (node.nodeType === Node.TEXT_NODE) {
            caretOffset += range.endOffset;
          } else {
            for (let i = 0; i < range.endOffset; i++) {
              countNode(node.childNodes[i]);
            }
          }
          nodeFound = true;
          return;
        }

        if (node.nodeType === Node.TEXT_NODE) {
          caretOffset += node.textContent.length;
        } else if (node.nodeName === "BR") {
          caretOffset += 1;
        } else {
          for (let i = 0; i < node.childNodes.length; i++) {
            walkNodes(node.childNodes[i]);
            if (nodeFound) break;
          }
        }
      };

      const countNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          caretOffset += node.textContent.length;
        } else if (node.nodeName === "BR") {
          caretOffset += 1;
        } else {
          for (let i = 0; i < node.childNodes.length; i++) {
            countNode(node.childNodes[i]);
          }
        }
      };

      walkNodes(element);
      return caretOffset;
    },

    setCaretOffset(element, offset) {
      const sel = window.getSelection();
      if (!sel) return;

      const range = document.createRange();
      let currentOffset = 0;
      let nodeFound = false;

      const walkNodes = (node) => {
        if (nodeFound) return;

        if (node.nodeType === Node.TEXT_NODE) {
          const textLen = node.textContent.length;
          if (currentOffset + textLen >= offset) {
            range.setStart(node, offset - currentOffset);
            range.collapse(true);
            nodeFound = true;
          } else {
            currentOffset += textLen;
          }
        } else if (node.nodeName === "BR") {
          if (currentOffset === offset) {
            range.setStartBefore(node);
            range.collapse(true);
            nodeFound = true;
          } else {
            currentOffset += 1;
          }
        } else {
          for (let i = 0; i < node.childNodes.length; i++) {
            walkNodes(node.childNodes[i]);
            if (nodeFound) break;
          }
        }
      };

      walkNodes(element);

      if (!nodeFound && element) {
        range.selectNodeContents(element);
        range.collapse(false);
      }

      sel.removeAllRanges();
      sel.addRange(range);
    },

    handleEditorClick(e) {
      const link = e.target.closest("a");
      if (link) {
        e.preventDefault();
        window.open(link.href, "_blank", "noopener,noreferrer");
      }
    },

    clearDebounce() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
    },

    onPopoverEnter() {
      this.notesApi.cancelHide();
    },
    onPopoverLeave() {
      this.notesApi.hideHover();
    },
    switchToEdit() {
      if (!this.isEditing) {
        this.notesApi.enableEdit();
        this.$nextTick(() => {
          if (this.$refs.editor) {
            this.$refs.editor.focus();
          }
        });
      }
    },

    async forceSave() {
      if (this.localText !== this.initialText) {
        const isEmpty = this.localText.trim() === "";
        const proposalNumber = this.targetProposalNumber;

        if (!proposalNumber) return;

        try {
          if (isEmpty && this.existingNote) {
            await this.deleteProfileNote({
              profileId: this.profileId,
              proposalNumber,
            });
          } else if (!isEmpty && this.existingNote) {
            await this.updateProfileNote({
              profileId: this.profileId,
              proposalNumber,
              text: this.localText,
            });
          } else if (!isEmpty && !this.existingNote) {
            await this.createProfileNote({
              profileId: this.profileId,
              proposalNumber,
              text: this.localText,
            });
          }

          this.initialText = this.localText;
        } catch (error) {
          console.error("Ошибка при сохранении заметки:", error);
        }
      }
    },

    async closeAndSave() {
      this.clearDebounce();
      await this.forceSave();
      this.notesApi.hide();
    },

    async removeNote() {
      this.clearDebounce();
      const proposalNumber = this.targetProposalNumber;

      try {
        if (this.existingNote && proposalNumber) {
          await this.deleteProfileNote({
            profileId: this.profileId,
            proposalNumber,
          });
        }
        this.localText = "";
        this.initialText = "";
        this.renderContent();
      } catch (error) {
        console.error("Ошибка при удалении заметки:", error);
      } finally {
        this.notesApi.hide();
      }
    },
  },
  created() {
    if (this.notesApi.setSaveCallback) {
      this.notesApi.setSaveCallback(this.forceSave);
    }
  },
  async beforeDestroy() {
    this.clearDebounce();
    await this.forceSave();
    this.notesApi.hide();
  },
};
</script>

<style lang="less" scoped>
.notes-popover-container {
  z-index: 100;
  transform-origin: top left;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
  }

  &::before {
    top: -10px;
    left: 9px;
    border-bottom: 10px solid #cfd8dd;
    border-right: 10px solid transparent;
    z-index: 1;
  }

  &::after {
    top: -6px;
    left: 11px;
    border-bottom: 8px solid #ffffff;
    border-right: 8px solid transparent;
    z-index: 2;
  }

  &.placement-bottom .notes-resizer-icon {
    right: 1px;
    bottom: 1px;
    cursor: nwse-resize;
  }

  &.placement-top {
    transform-origin: bottom left;

    &::before {
      top: auto;
      bottom: -10px;
      border-bottom: none;
      border-top: 10px solid #cfd8dd;
    }

    &::after {
      top: auto;
      bottom: -6px;
      border-bottom: none;
      border-top: 8px solid #ffffff;
    }

    .notes-resizer-icon {
      right: 1px;
      top: 1px;
      cursor: nesw-resize;
      transform: scaleY(-1);
    }
  }
}

.custom-notes-popover {
  width: 304px;
  height: 163px;
  min-width: 220px;
  min-height: 140px;
  background-color: #ffffff;
  border-radius: 6px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);
  border: 2px solid #cfd8dd;
  box-sizing: border-box;
  resize: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 8px 8px 30px 8px;
  position: relative;

  &.is-view-mode {
    cursor: pointer;
  }
}

.notes-editor {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: 0 12px 0 0;
  border: none;
  background: transparent;
  font-family: Roboto, sans-serif;
  font-weight: 400;
  font-style: italic;
  font-size: 14px;
  line-height: 120%;
  letter-spacing: 0%;
  color: #7d919b;
  outline: none;
  position: relative;
  z-index: 2;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;

  &:empty::before {
    content: attr(placeholder);
    color: #b0bec5;
    pointer-events: none;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
    background-color: #f7f7f7;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #cfd8dd;
    border-radius: 4px;
    min-height: 31px;
  }

  /deep/ .notes-link {
    color: #1e88e5;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: #1565c0;
    }
  }
}

.notes-resizer-icon {
  position: absolute;
  width: 14px;
  height: 14px;
  z-index: 4;
  pointer-events: auto;
}

.delete-btn {
  position: absolute;
  left: 3px;
  bottom: 3px;
  z-index: 3;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  width: 24px;
  height: 24px;
}

.char-counter {
  position: absolute;
  right: 30px;
  bottom: 6px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  color: #b0bec5;
  user-select: none;
  pointer-events: none;
  z-index: 3;
}

.notes-fade-enter-active,
.notes-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.notes-fade-enter,
.notes-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
